import { useEffect, useState, useCallback } from 'react';
import { socketManager, WsEventListener } from '@/services/websocket/socketManager';
import { WsConnectionState, WsBaseEvent } from '@/types/websocket';
import { APP_CONFIG } from '@/lib/constants';

export function useWebSocket(onEvent?: WsEventListener) {
  const [connectionState, setConnectionState] = useState<WsConnectionState>(
    socketManager.getState()
  );

  useEffect(() => {
    const unsubscribeState = socketManager.subscribeState((state) => {
      setConnectionState(state);
    });

    let unsubscribeEvent: (() => void) | undefined;
    if (onEvent) {
      unsubscribeEvent = socketManager.subscribeEvents(onEvent);
    }

    // Auto-connect with configured fallback mode
    if (APP_CONFIG.enableMockFallback) {
      socketManager.setMockMode(true);
    } else {
      socketManager.connect();
    }

    // Handle browser visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const current = socketManager.getState();
        if (current.status === 'OFFLINE') {
          socketManager.reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeState();
      if (unsubscribeEvent) unsubscribeEvent();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onEvent]);

  const reconnect = useCallback(() => {
    socketManager.reconnect();
  }, []);

  const disconnect = useCallback(() => {
    socketManager.disconnect();
  }, []);

  const setMockMode = useCallback((active: boolean) => {
    socketManager.setMockMode(active);
  }, []);

  const broadcastEvent = useCallback((event: WsBaseEvent) => {
    socketManager.processIncomingEvent(event);
  }, []);

  return {
    status: connectionState.status,
    reconnectAttempt: connectionState.reconnectAttempt,
    lastEventTime: connectionState.lastEventTime,
    lastError: connectionState.lastError,
    reconnect,
    disconnect,
    setMockMode,
    broadcastEvent,
    isLive: connectionState.status === 'LIVE',
    isReconnecting: connectionState.status === 'RECONNECTING',
    isOffline: connectionState.status === 'OFFLINE',
  };
}
